import { useEffect, useRef, useState } from "react";

/**
 * No-surprises rule (spec §9.19, D-23): a widget's *displayed* ordering is frozen while the pointer
 * is inside it; incoming changes are applied when the pointer leaves, after 30 s idle, or on click.
 * Data is always current — only the visible arrangement waits.
 */
export function useDeferredValue<T>(live: T, same: (a: T, b: T) => boolean, idleMs = 30_000) {
  const [frozen, setFrozen] = useState<T | null>(null);
  const lastMove = useRef(0);
  const hovering = useRef(false);
  const value = frozen ?? live;
  const pendingCount = frozen !== null && !same(frozen, live) ? 1 : 0;

  useEffect(() => {
    if (frozen === null) return;
    const h = setInterval(() => {
      if (Date.now() - lastMove.current > idleMs) setFrozen(null);
    }, 1000);
    return () => clearInterval(h);
  }, [frozen, idleMs]);

  return {
    value,
    pendingCount,
    /** apply pending changes now (also after the person's own action, so their change shows) */
    apply: () => setFrozen(null),
    hoverProps: {
      onPointerEnter: () => {
        hovering.current = true;
        lastMove.current = Date.now();
        setFrozen(live);
      },
      onPointerMove: () => {
        lastMove.current = Date.now();
        // after an apply() the next movement freezes the new arrangement again
        if (hovering.current && frozen === null) setFrozen(live);
      },
      onPointerLeave: () => {
        hovering.current = false;
        setFrozen(null);
      },
    },
  };
}
